<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
// import HumidityChart from '@/components/charts/HumidityChart.vue' // HIDDEN: Para uso futuro
import OxygenChart from '@/components/charts/OxygenChart.vue'
import TemperatureGaugeChart from '@/components/charts/TemperatureGaugeChart.vue'
import GrowthChart from '@/components/charts/GrowthChart.vue'
// import HumidityOxygenLineChart from '@/components/charts/HumidityOxygenLineChart.vue' // HIDDEN: Para uso futuro
import BatteryChart from '@/components/charts/BatteryChart.vue'
import TemperatureChart from '@/components/charts/TemperatureChart.vue'
import TemperatureOxygenChart from '@/components/charts/TemperatureOxygenChart.vue'
import RootTimelapse from '@/components/RootTimelapse.vue'
import { useDeviceSensorManager, type DeviceSensorData } from '@/composables/useDeviceSensorManager'
import { useMeasurementsStore } from '@/views/DeviceSamples/store'
import { DeviceSample } from '@/views/DeviceSamples/models/deviceSample'
import { useDeviceStore } from '@/views/Devices/store'
import { Device } from '@/views/Devices/models/device'
import { useRoute } from 'vue-router'
import { useDeviceLoader } from '@/composables/useDeviceLoader'

// Configuración y manejo de datos reales
const route = useRoute()
const deviceId = route.params.id as string || 'default-device-id' // Obtener del route o usar valor por defecto

// Stores
const sensorManager = useDeviceSensorManager()
const measurementsStore = useMeasurementsStore()
const deviceStore = useDeviceStore()
const deviceLoader = useDeviceLoader()

// Estados reactivos para datos reales
const realSensorData = ref<DeviceSensorData>({
    temperatureData: [],
    oxygenData: [],
    batteryData: [],
    temperatureOxygenData: [],
    lastValues: {}
})

const realGrowthData = ref<Array<{ date: string, growth: number, newRoots: number }>>([])
const deviceMeasurements = ref<DeviceSample[]>([])
const showAllMeasurements = ref(false)
const currentDevice = ref<Device | null>(null)
const selectedSample = ref<DeviceSample | null>(null)
const showSampleDialog = ref(false)

// Ordenar mediciones por time desc (más reciente primero)
const sortedMeasurements = computed(() => {
    return [...deviceMeasurements.value].sort(
        (a, b) => new Date(b.time).getTime() - new Date(a.time).getTime()
    )
})

const openSampleDialog = (sample: DeviceSample) => {
    selectedSample.value = sample
    showSampleDialog.value = true
}

// Loading states
const loadingRealData = ref(false)
const errorMessage = ref<string | null>(null)

// Función para calcular el nivel de batería desde voltaje
// Fórmula: Rango típico de batería 12V es 10.5V (0%) a 14.4V (100%)
const calculateBatteryLevel = (voltage: number): number => {
    const minVoltage = 10.5  // 0% batería
    const maxVoltage = 14.4  // 100% batería

    if (voltage <= minVoltage) return 0
    if (voltage >= maxVoltage) return 100

    const level = ((voltage - minVoltage) / (maxVoltage - minVoltage)) * 100
    return Math.round(level)
}

// Computed property para generar growth data desde deviceMeasurements reales
const computedGrowthDataFromMeasurements = computed(() => {
    return deviceMeasurements.value
        .filter(sample => sample.managed && sample.growthPercent !== null && sample.growthPercent !== undefined)
        .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
        .map(sample => ({
            date: new Date(sample.createdAt).toISOString().split('T')[0],
            growth: sample.growthPercent || 0,
            newRoots: sample.newRootsCount || 0
        }))
})

// Función para cargar todos los datos del dispositivo
const loadDeviceMeasurements = async () => {
    loadingRealData.value = true
    errorMessage.value = null

    try {
        // console.log('[DeviceView] ========== INICIO CARGA DE DATOS ==========')
        // console.log('[DeviceView] Loading data for device:', deviceId)

        // Primero, cargar los datos del dispositivo desde el store
        if (!deviceStore.devices || deviceStore.devices.length === 0) {
            // console.log('[DeviceView] Cargando lista de devices desde store...')
            await deviceLoader.loadDevicesByRole()
        }

        const foundDevice = deviceStore.devices.find(d => d.id === deviceId)
        if (foundDevice) {
            currentDevice.value = foundDevice
            // console.log('[DeviceView] Device encontrado:', foundDevice.name || foundDevice.id)
        } else {
            // console.log('[DeviceView] Device NO encontrado en store')
        }

        // Cargar datos de sensores, crecimiento y mediciones en paralelo
        // console.log('[DeviceView] Iniciando carga paralela de sensorData, growthData y deviceSamples...')
        const [sensorData, growthData, deviceSamples] = await Promise.all([
            sensorManager.loadDeviceSensorData(deviceId),
            sensorManager.loadGrowthData(deviceId),
            measurementsStore.getMeasurementsByDeviceId(deviceId)
        ])

        // console.log('[DeviceView] Resultado sensorData:', {
        //     temperatureCount: sensorData.temperatureData.length,
        //     oxygenCount: sensorData.oxygenData.length,
        //     batteryCount: sensorData.batteryData.length,
        //     temperatureOxygenCount: sensorData.temperatureOxygenData.length,
        //     lastValues: sensorData.lastValues
        // })

        realSensorData.value = sensorData
        realGrowthData.value = growthData
        deviceMeasurements.value = deviceSamples

        // console.log('[DeviceView] DeviceSamples loaded for device:', deviceId, {
        //     measurementsCount: deviceSamples.length,
        //     managedCount: deviceSamples.filter(m => m.managed).length,
        //     unManagedCount: deviceSamples.filter(m => !m.managed).length
        // })

        // Actualizar el objeto device con los últimos valores reales
        if (sensorData.lastValues.temperature !== undefined) {
            device.value.sensors.temperature = sensorData.lastValues.temperature
        }
        if (sensorData.lastValues.oxygen !== undefined) {
            device.value.sensors.oxygen = sensorData.lastValues.oxygen
        }
        if (sensorData.lastValues.batteryLevel !== undefined) {
            device.value.battery.level = sensorData.lastValues.batteryLevel
        }
        if (sensorData.lastValues.voltage !== undefined) {
            device.value.battery.voltage = sensorData.lastValues.voltage
        }

        // console.log('[DeviceView] Device data loaded successfully:', {
        //     temperaturePoints: sensorData.temperatureData.length,
        //     oxygenPoints: sensorData.oxygenData.length,
        //     batteryPoints: sensorData.batteryData.length,
        //     batteryDataSample: sensorData.batteryData.slice(0, 3),
        //     growthPoints: growthData.length,
        //     lastValues: sensorData.lastValues
        // })
        // console.log('[DeviceView] ========== FIN CARGA DE DATOS ==========')

    } catch (error) {
        errorMessage.value = error instanceof Error ? error.message : 'Error al cargar datos del dispositivo'
        console.error('[DeviceView] Error loading device data:', error)
    } finally {
        loadingRealData.value = false
    }
}

// Sensor readings data - UNUSED: Datos de ejemplo para referencia
/*
const sensorReadings = ref([
    {
        temperature: 22.5,
        oxygen: 18.3,
        humidity: 65,
        timestamp: '2024-02-15 15:00',
        batteryLevel: 78,
        growth: 3.2, // Algunos tienen crecimiento
        newRoots: 2
    },
    {
        temperature: 21.8,
        oxygen: 17.9,
        humidity: 68,
        timestamp: '2024-02-15 14:30',
        batteryLevel: 79,
        // Sin crecimiento en esta lectura
    },
    {
        temperature: 23.1,
        oxygen: 18.7,
        humidity: 62,
        timestamp: '2024-02-15 14:00',
        batteryLevel: 79,
        growth: 2.8,
        newRoots: 1
    },
    {
        temperature: 22.9,
        oxygen: 18.1,
        humidity: 64,
        timestamp: '2024-02-15 13:30',
        batteryLevel: 80,
        // Sin crecimiento
    },
    {
        temperature: 22.3,
        oxygen: 17.8,
        humidity: 66,
        timestamp: '2024-02-15 13:00',
        batteryLevel: 80,
        // Sin crecimiento
    },
    {
        temperature: 21.9,
        oxygen: 18.2,
        humidity: 67,
        timestamp: '2024-02-15 12:30',
        batteryLevel: 81,
        growth: 1.5,
        newRoots: 3
    },
    {
        temperature: 22.7,
        oxygen: 17.5,
        humidity: 63,
        timestamp: '2024-02-15 12:00',
        batteryLevel: 82,
    },
    {
        temperature: 21.5,
        oxygen: 18.9,
        humidity: 69,
        timestamp: '2024-02-15 11:30',
        batteryLevel: 82,
    },
    {
        temperature: 20.8,
        oxygen: 19.1,
        humidity: 71,
        timestamp: '2024-02-15 11:00',
        batteryLevel: 83,
        growth: 1.2,
        newRoots: 1
    },
    {
        temperature: 21.2,
        oxygen: 18.6,
        humidity: 68,
        timestamp: '2024-02-15 10:30',
        batteryLevel: 83,
    },
    {
        temperature: 22.1,
        oxygen: 17.7,
        humidity: 64,
        timestamp: '2024-02-15 10:00',
        batteryLevel: 84,
    },
    {
        temperature: 23.4,
        oxygen: 16.9,
        humidity: 59,
        timestamp: '2024-02-15 09:30',
        batteryLevel: 84,
    }
])
*/

// Growth data for chart - UNUSED: Datos de ejemplo para referencia
/*
const growthData = ref([
    { date: '2024-02-10', growth: 2.1, newRoots: 1 },
    { date: '2024-02-11', growth: 3.5, newRoots: 2 },
    { date: '2024-02-12', growth: 1.8, newRoots: 1 },
    { date: '2024-02-13', growth: 4.2, newRoots: 3 },
    { date: '2024-02-14', growth: 2.9, newRoots: 2 },
    { date: '2024-02-15', growth: 3.2, newRoots: 2 },
])
*/

// Datos para gráfico de batería - UNUSED: Datos de ejemplo para referencia
/*
const batteryData = ref([
    { timestamp: '2024-02-15T08:00:00Z', batteryLevel: 95, voltage: 12.4 },
    { timestamp: '2024-02-15T09:00:00Z', batteryLevel: 92, voltage: 12.3 },
    { timestamp: '2024-02-15T10:00:00Z', batteryLevel: 89, voltage: 12.2 },
    { timestamp: '2024-02-15T11:00:00Z', batteryLevel: 86, voltage: 12.1 },
    { timestamp: '2024-02-15T12:00:00Z', batteryLevel: 82, voltage: 12.0 },
    { timestamp: '2024-02-15T13:00:00Z', batteryLevel: 78, voltage: 11.9 },
])

// Datos para gráfico de temperatura simple
const temperatureData = ref([
    { timestamp: '2024-02-15T08:00:00Z', temperature: 22.5 },
    { timestamp: '2024-02-15T09:00:00Z', temperature: 23.1 },
    { timestamp: '2024-02-15T10:00:00Z', temperature: 24.0 },
    { timestamp: '2024-02-15T11:00:00Z', temperature: 25.2 },
    { timestamp: '2024-02-15T12:00:00Z', temperature: 26.8 },
    { timestamp: '2024-02-15T13:00:00Z', temperature: 23.4 },
])

// Datos para gráfico combinado temperatura-oxígeno
const temperatureOxygenData = ref([
    { timestamp: '2024-02-15T08:00:00Z', temperature: 22.5, oxygen: 21.2 },
    { timestamp: '2024-02-15T09:00:00Z', temperature: 23.1, oxygen: 20.8 },
    { timestamp: '2024-02-15T10:00:00Z', temperature: 24.0, oxygen: 20.5 },
    { timestamp: '2024-02-15T11:00:00Z', temperature: 25.2, oxygen: 20.1 },
    { timestamp: '2024-02-15T12:00:00Z', temperature: 26.8, oxygen: 19.7 },
    { timestamp: '2024-02-15T13:00:00Z', temperature: 23.4, oxygen: 20.9 },
])
*/

// Computed properties for chart data - UNUSED: Para ejemplo futuro
// const sortedTopics = computed(() => {
//     return [...topicData.value].sort((a, b) => b.percentage - a.percentage)
// })
// Últimas 3 imágenes reales de DeviceSamples (que tengan imageUrl)
const carouselImages = computed(() => {
    const samplesWithImage = deviceMeasurements.value
        .filter(sample => sample.imageUrl && sample.imageUrl.trim() !== '')
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        .slice(0, 3)
        .map(sample => sample.imageUrl)
    return samplesWithImage.length > 0 ? samplesWithImage : ['/images/1.png']
})

const currentSlide = ref(0)
let carouselInterval: number | null = null

// Start the automatic carousel (solo si hay más de 1 imagen)
const startCarousel = () => {
    if (carouselImages.value.length <= 1) return
    carouselInterval = window.setInterval(() => {
        currentSlide.value = (currentSlide.value + 1) % carouselImages.value.length
    }, 3000)
}

// Clean up interval when component unmounts
onMounted(async () => {
    // Cargar datos reales del dispositivo (las imágenes vienen de aquí)
    await loadDeviceMeasurements()
    // Iniciar carrusel después de tener las imágenes reales
    currentSlide.value = 0
    startCarousel()
})

onBeforeUnmount(() => {
    if (carouselInterval) {
        clearInterval(carouselInterval)
    }
})

// Device data - Fotorizotron
const device = ref({
    id: 1,
    name: 'Fotorizotron - Hectarea 5',
    location: 'Atacama, Centro',
    icon: 'mdi-camera-outline',
    iconBg: '#E8F5E8',
    iconColor: '#4CAF50',
    battery: {
        level: 78,
        status: 'Good',
        estimatedDays: 12,
        voltage: 12.1
    },
    lastPhoto: {
        timestamp: '2024-02-15 14:30',
        path: '/images/roots/latest.jpg'
    },
    sensors: {
        temperature: 22.5,
        oxygen: 18.3,
        humidity: 65,
        lastUpdate: '2024-02-15 15:00'
    },
    rootAnalysis: {
        growthPercentage: 15.4,
        newRootsCount: 8,
        totalRootsDetected: 45,
        healthScore: 8.2,
        suggestedNextCapture: '2024-02-16 08:00'
    },
    status: {
        online: true,
        lastConnection: '2024-02-15 15:00',
        operatingDays: 45
    },
    tag: {
        text: 'Active',
        color: '#4CAF50'
    }
})

// Format budget display - UNUSED: Para ejemplo futuro
// const formatBudget = (total: number, used: number) => {
//     const formatValue = (value: number) => {
//         if (value >= 1000) {
//             return `$${(value / 1000).toFixed(1)}k`
//         }
//         return `$${value}`
//     }
//     return `${formatValue(used)} of ${formatValue(total)}`
// }
</script>

<template>
    <v-container fluid class="device-view-container">
        <v-row class="device-layout">
            <!-- Data and charts - Scrollable column -->
            <v-col cols="12" md="6" class="scrollable-column">
                <v-row>
                    <!-- Welcome section -->
                    <v-col cols="12" md="8">
                        <v-card class="pa-6" flat elevation="3">
                            <div class="d-flex flex-column">
                                <div class="text-h5 font-weight-bold">
                                    {{ currentDevice?.name || device.name }}
                                </div>
                                <div class="text-body-1 text-medium-emphasis mt-1">
                                    Estado: {{ currentDevice?.active ? 'Activo' : 'Inactivo' }} •
                                    Última actualización: {{ currentDevice?.updatedAt ? new
                                        Date(currentDevice.updatedAt).toLocaleDateString('es-ES') : 'N/A' }}
                                </div>

                                <v-row class="mt-6">
                                    <!-- Root Growth -->
                                    <v-col cols="4">
                                        <v-card rounded="lg" flat class="pa-4" color="#F5F3FF">
                                            <div class="d-flex">
                                                <v-icon color="#8465FF" size="30"
                                                    class="pa-2 rounded bg-purple-lighten-5">mdi-trending-up</v-icon>
                                                <div class="ml-auto text-h5 font-weight-bold">
                                                    {{ computedGrowthDataFromMeasurements.length > 0 ?
                                                        computedGrowthDataFromMeasurements[computedGrowthDataFromMeasurements.length
                                                            - 1].growth : 0 }}%
                                                </div>
                                            </div>
                                            <div class="text-subtitle-1 mt-2">Crecimiento</div>
                                        </v-card>
                                    </v-col>

                                    <!-- New Roots Count -->
                                    <v-col cols="4">
                                        <v-card rounded="lg" flat class="pa-4" color="#EAF6FF">
                                            <div class="d-flex">
                                                <v-icon color="#4CB3FF" size="30"
                                                    class="pa-2 rounded bg-blue-lighten-5">mdi-sprout</v-icon>
                                                <div class="ml-auto text-h5 font-weight-bold">
                                                    {{ computedGrowthDataFromMeasurements.length > 0 ?
                                                        computedGrowthDataFromMeasurements[computedGrowthDataFromMeasurements.length
                                                            - 1].newRoots : 0 }}
                                                </div>
                                            </div>
                                            <div class="text-subtitle-1 mt-2">Raíces Nuevas</div>
                                        </v-card>
                                    </v-col>

                                    <!-- Root Health Score -->
                                    <v-col cols="4">
                                        <v-card rounded="lg" flat class="pa-4" color="#FFF8EA">
                                            <div class="d-flex">
                                                <v-icon color="#FFA726" size="30"
                                                    class="pa-2 rounded bg-amber-lighten-5">mdi-heart-pulse</v-icon>
                                                <div class="ml-auto text-h5 font-weight-bold">
                                                    <!-- {{deviceMeasurements.filter(m => m.managed).length > 0 ?
                                                        Math.round(deviceMeasurements.filter(m => m.managed).reduce((sum, m)
                                                            => sum + (m.rootHealthScore || 0), 0) / deviceMeasurements.filter(m
                                                    => m.managed).length)
                                                    : 0 }}/10 -->
                                                </div>
                                            </div>
                                            <div class="text-subtitle-1 mt-2">Salud Raíz</div>
                                        </v-card>
                                    </v-col>
                                </v-row>
                            </div>
                        </v-card>
                    </v-col>

                    <!-- Battery and Sensor Status -->
                    <v-col cols="12" md="4">
                        <v-card class="pa-6" flat elevation="3">
                            <div class="d-flex flex-column">
                                <div class="text-h6 font-weight-bold">Estado del Dispositivo</div>
                                <div class="text-body-2 text-medium-emphasis">{{ device.battery.status }} • {{
                                    device.battery.estimatedDays }} días restantes</div>

                                <div class="d-flex justify-center mt-4 position-relative">
                                    <v-progress-circular size="160" width="12"
                                        :color="device.battery.level > 30 ? '#4CAF50' : '#FF5722'"
                                        :model-value="device.battery.level">
                                        <div class="text-center">
                                            <div class="text-h5 font-weight-bold">{{ device.battery.level }}%</div>
                                            <div class="text-caption text-medium-emphasis">Batería</div>
                                        </div>
                                    </v-progress-circular>
                                </div>

                                <!-- Sensor readings -->
                                <div class="mt-4">
                                    <div class="d-flex justify-space-between align-center mb-2">
                                        <span class="text-body-2">Temperatura:</span>
                                        <span class="text-body-1 font-weight-medium">{{ device.sensors.temperature
                                            }}°C</span>
                                    </div>
                                    <div class="d-flex justify-space-between align-center mb-2">
                                        <span class="text-body-2">Oxígeno:</span>
                                        <span class="text-body-1 font-weight-medium">{{ device.sensors.oxygen }}%</span>
                                    </div>
                                    <!-- HIDDEN: Métrica de humedad para uso futuro
                                    <div class="d-flex justify-space-between align-center">
                                        <span class="text-body-2">Humedad:</span>
                                        <span class="text-body-1 font-weight-medium">{{ device.sensors.humidity }}%</span>
                                    </div>
                                    -->
                                    <div class="d-flex justify-space-between align-center">
                                        <span class="text-body-2">Voltaje:</span>
                                        <span class="text-body-1 font-weight-medium">{{ device.battery.voltage
                                            }}V</span>
                                    </div>
                                </div>
                            </div>
                        </v-card>
                    </v-col>
                </v-row>
                <!-- Sensor Charts Section -->
                <v-row class="mt-6">
                    <!-- Temperature Chart -->
                    <v-col cols="12">
                        <v-card flat elevation="3" class="pa-4">
                            <v-card-title class="text-h6 font-weight-bold pa-0 mb-2">
                                Temperatura
                            </v-card-title>
                            <!-- Loading state para temperatura -->
                            <div v-if="loadingRealData" class="d-flex justify-center align-center"
                                style="height: 250px;">
                                <v-progress-circular indeterminate color="primary"></v-progress-circular>
                            </div>
                            <!-- Error state -->
                            <div v-else-if="errorMessage" class="d-flex justify-center align-center text-center pa-4"
                                style="height: 250px;">
                                <div>
                                    <v-icon color="error" size="48" class="mb-2">mdi-alert-circle</v-icon>
                                    <div class="text-body-2 text-error">{{ errorMessage }}</div>
                                    <v-btn size="small" color="primary" class="mt-2" @click="loadDeviceMeasurements">
                                        Reintentar
                                    </v-btn>
                                </div>
                            </div>
                            <!-- Datos reales de temperatura -->
                            <TemperatureChart v-else :temperature-data="realSensorData.temperatureData"
                                title="Historial de Temperatura" height="250px" />
                        </v-card>
                    </v-col>

                    <!-- Oxygen Chart -->
                    <v-col cols="12">
                        <v-row>
                            <!-- chart actual oxigeno -->
                            <v-col>
                                <v-card flat elevation="3" class="pa-4">
                                    <v-card-title class="text-h6 font-weight-bold pa-0 mb-2">
                                        Oxígeno Actual
                                    </v-card-title>
                                    <v-progress-circular v-if="loadingRealData" indeterminate color="primary"
                                        class="ma-4"></v-progress-circular>
                                    <v-alert v-else-if="errorMessage" type="error" dense class="ma-2">
                                        Error cargando datos de oxígeno: {{ errorMessage }}
                                    </v-alert>
                                    <OxygenChart
                                        v-else-if="realSensorData.oxygenData && realSensorData.oxygenData.length > 0"
                                        :oxygen="realSensorData.oxygenData[realSensorData.oxygenData.length - 1]?.oxygen || 0" />
                                    <OxygenChart v-else :oxygen="device.sensors.oxygen" />
                                </v-card>
                            </v-col>
                            <!-- chart actual temperatura -->
                            <v-col>

                                <v-card flat elevation="3" class="pa-4">
                                    <v-card-title class="text-h6 font-weight-bold pa-0 mb-2">
                                        Temperatura Actual
                                    </v-card-title>
                                    <v-progress-circular v-if="loadingRealData" indeterminate color="primary"
                                        class="ma-4"></v-progress-circular>
                                    <v-alert v-else-if="errorMessage" type="error" dense class="ma-2">
                                        Error cargando datos de temperatura: {{ errorMessage }}
                                    </v-alert>

                                    <TemperatureGaugeChart :temperature="device.sensors.temperature" />
                                </v-card>

                            </v-col>
                        </v-row>

                    </v-col>

                    <!-- Battery Chart -->
                    <v-col cols="12">
                        <v-card flat elevation="3" class="pa-4">
                            <v-card-title class="text-h6 font-weight-bold pa-0 mb-2">
                                Estado de Batería
                            </v-card-title>
                            <v-progress-circular v-if="loadingRealData" indeterminate color="primary"
                                class="ma-4"></v-progress-circular>
                            <v-alert v-else-if="errorMessage" type="error" dense class="ma-2">
                                Error cargando datos de batería: {{ errorMessage }}
                            </v-alert>
                            <BatteryChart
                                v-else-if="realSensorData.batteryData && realSensorData.batteryData.length > 0"
                                :battery-data="realSensorData.batteryData.map(d => ({
                                    timestamp: d.timestamp,
                                    batteryLevel: calculateBatteryLevel(d.voltage || 12),
                                    voltage: d.voltage || 0
                                }))" title="Voltaje de Batería" height="250px" :show-voltage="true" />
                            <div v-else class="text-center pa-8">
                                <v-icon size="48" color="grey-lighten-2">mdi-battery-outline</v-icon>
                                <div class="text-body-2 text-medium-emphasis mt-2">Sin datos de batería disponibles
                                </div>
                            </div>
                        </v-card>
                    </v-col>
                </v-row>

                <!-- Growth Chart Section -->
                <v-row class="mt-6">
                    <v-col cols="12">
                        <v-card flat elevation="3" class="pa-4">
                            <v-card-title class="text-h6 font-weight-bold pa-0 mb-2">
                                Historial de Crecimiento
                            </v-card-title>
                            <v-progress-circular v-if="loadingRealData" indeterminate color="primary"
                                class="ma-4"></v-progress-circular>
                            <v-alert v-else-if="errorMessage" type="error" dense class="ma-2">
                                Error cargando datos de crecimiento: {{ errorMessage }}
                            </v-alert>
                            <div v-else-if="computedGrowthDataFromMeasurements.length === 0" class="text-center pa-8">
                                <v-icon size="48" color="grey-lighten-2">mdi-chart-line</v-icon>
                                <div class="text-body-2 text-medium-emphasis mt-2">Sin datos de crecimiento disponibles
                                </div>
                                <div class="text-caption text-grey">Las mediciones analizadas aparecerán aquí</div>
                            </div>
                            <GrowthChart v-else :growth-data="computedGrowthDataFromMeasurements" />
                        </v-card>
                    </v-col>

                    <!-- Root Timelapse -->
                    <v-col cols="12">
                        <v-card flat elevation="3" class="pa-4">
                            <v-card-title class="text-h6 font-weight-bold pa-0 mb-2">
                                <v-icon class="mr-2">mdi-timelapse</v-icon>
                                Timelapse de Crecimiento
                            </v-card-title>
                            <v-card-subtitle class="pa-0 mb-4 text-body-2 text-medium-emphasis">
                                Secuencia cronológica de fotos de raíces
                            </v-card-subtitle>
                            <v-progress-circular v-if="loadingRealData" indeterminate color="primary"
                                class="ma-4"></v-progress-circular>
                            <RootTimelapse v-else :samples="deviceMeasurements" />
                        </v-card>
                    </v-col>

                    <!-- Combined Temperature-Oxygen Chart -->
                    <v-col cols="12">
                        <v-card flat elevation="3" class="pa-4">
                            <v-card-title class="text-h6 font-weight-bold pa-0 mb-2">
                                Tendencia Temperatura-Oxígeno
                            </v-card-title>
                            <v-card-subtitle class="pa-0 mb-4 text-body-2 text-medium-emphasis">
                                Correlación de temperatura y oxígeno en el tiempo
                            </v-card-subtitle>
                            <v-progress-circular v-if="loadingRealData" indeterminate color="primary"
                                class="ma-4"></v-progress-circular>
                            <v-alert v-else-if="errorMessage" type="error" dense class="ma-2">
                                Error cargando datos combinados: {{ errorMessage }}
                            </v-alert>
                            <TemperatureOxygenChart
                                v-else-if="realSensorData.temperatureOxygenData && realSensorData.temperatureOxygenData.length > 0"
                                :sensor-data="realSensorData.temperatureOxygenData" title="Temperatura vs Oxígeno"
                                height="300px" />
                            <div v-else class="text-center pa-8">
                                <v-icon size="48" color="grey-lighten-2">mdi-chart-line-variant</v-icon>
                                <div class="text-body-2 text-medium-emphasis mt-2">Sin datos disponibles</div>
                            </div>
                        </v-card>
                    </v-col>
                </v-row>

                <!-- HIDDEN: Humidity charts para uso futuro -->
                <!--
                <v-row class="mt-6" v-if="false">
                    <v-col cols="12">
                        <v-card flat elevation="3" class="pa-4">
                            <v-card-title class="text-h6 font-weight-bold pa-0 mb-2">
                                Tendencia de Humedad y Oxígeno en el Tiempo
                            </v-card-title>
                            <v-card-subtitle class="pa-0 mb-4 text-body-2 text-medium-emphasis">
                                Evolución de los niveles de humedad y oxígeno durante las últimas lecturas
                            </v-card-subtitle>
                            <HumidityOxygenLineChart :sensor-data="sensorReadings" />
                        </v-card>
                    </v-col>
                </v-row>
                -->

                <!-- Detailed DeviceSamples -->
                <v-card flat class="mt-6" elevation="3">
                    <v-card-title class="d-flex justify-space-between align-center pa-6">
                        <span class="text-h6 font-weight-bold">Mediciones del Dispositivo</span>
                        <v-btn icon variant="text" @click="loadDeviceMeasurements">
                            <v-icon>mdi-refresh</v-icon>
                        </v-btn>
                    </v-card-title>

                    <v-card-text>
                        <!-- Loading state -->
                        <div v-if="loadingRealData" class="d-flex justify-center align-center pa-8">
                            <v-progress-circular indeterminate color="primary" size="40"></v-progress-circular>
                            <span class="ml-3">Cargando mediciones...</span>
                        </div>

                        <!-- Error state -->
                        <v-alert v-else-if="errorMessage" type="error" class="ma-4">
                            {{ errorMessage }}
                        </v-alert>

                        <!-- DeviceSamples table -->
                        <div v-else>
                            <!-- Header Row -->
                            <v-row class="mb-4">
                                <v-col cols="2">
                                    <div class="text-body-2 font-weight-medium">Fecha</div>
                                </v-col>
                                <v-col cols="2">
                                    <div class="text-body-2 font-weight-medium">Estado</div>
                                </v-col>
                                <v-col cols="2">
                                    <div class="text-body-2 font-weight-medium">Crecimiento</div>
                                </v-col>
                                <v-col cols="2">
                                    <div class="text-body-2 font-weight-medium">Salud</div>
                                </v-col>
                                <v-col cols="2">
                                    <div class="text-body-2 font-weight-medium">Nuevas Raíces</div>
                                </v-col>
                                <v-col cols="2">
                                    <div class="text-body-2 font-weight-medium">Análisis</div>
                                </v-col>
                            </v-row>

                            <div class="deviceSamples-container" style="max-height: 400px; overflow-y: auto;">
                                <v-row
                                    v-for="deviceSample in (showAllMeasurements ? sortedMeasurements : sortedMeasurements.slice(0, 5))"
                                    :key="deviceSample.id" class="mb-2 deviceSample-row pa-2"
                                    style="cursor: pointer;"
                                    :class="{ 'managed': deviceSample.managed, 'unmanaged': !deviceSample.managed }"
                                    @click="openSampleDialog(deviceSample)">
                                    <!-- Fecha (campo time) -->
                                    <v-col cols="2" class="d-flex align-center">
                                        <div class="text-body-2">
                                            {{ new Date(deviceSample.time).toLocaleDateString('es-ES') }}
                                            <div class="text-caption text-grey">
                                                {{ new Date(deviceSample.time).toLocaleTimeString('es-ES', {
                                                    hour: '2-digit', minute: '2-digit'
                                                }) }}
                                            </div>
                                        </div>
                                    </v-col>

                                    <!-- Estado -->
                                    <v-col cols="2" class="d-flex align-center">
                                        <v-chip size="small" :color="deviceSample.managed ? 'success' : 'warning'"
                                            :text-color="deviceSample.managed ? 'white' : 'black'">
                                            {{ deviceSample.managed ? 'Analizada' : 'Pendiente' }}
                                        </v-chip>
                                    </v-col>

                                    <!-- Crecimiento -->
                                    <v-col cols="2" class="d-flex align-center">
                                        <div v-if="deviceSample.managed">
                                            <v-chip size="small"
                                                :color="deviceSample.growthPercent >= 3 ? 'success' : deviceSample.growthPercent >= 1 ? 'warning' : 'error'">
                                                {{ deviceSample.growthPercent }}%
                                            </v-chip>
                                            <div class="text-caption text-grey mt-1">
                                                {{ deviceSample.getGrowthCategory() }}
                                            </div>
                                        </div>
                                        <div v-else class="text-caption text-grey">
                                            Sin análisis
                                        </div>
                                    </v-col>

                                    <!-- Salud -->
                                    <v-col cols="2" class="d-flex align-center">
                                        <div v-if="deviceSample.managed">
                                            <v-chip size="small"
                                                :color="deviceSample.rootHealthScore >= 8 ? 'success' : deviceSample.rootHealthScore >= 6 ? 'warning' : 'error'">
                                                {{ deviceSample.rootHealthScore }}/10
                                            </v-chip>
                                        </div>
                                        <div v-else class="text-caption text-grey">
                                            Sin análisis
                                        </div>
                                    </v-col>

                                    <!-- Nuevas Raíces -->
                                    <v-col cols="2" class="d-flex align-center">
                                        <div v-if="deviceSample.managed">
                                            <v-chip size="small" color="info">
                                                {{ deviceSample.newRootsCount }}
                                            </v-chip>
                                        </div>
                                        <div v-else class="text-caption text-grey">
                                            Sin análisis
                                        </div>
                                    </v-col>

                                    <!-- Acción -->
                                    <v-col cols="2" class="d-flex align-center">
                                        <v-btn size="small" color="primary" variant="text"
                                            @click.stop="openSampleDialog(deviceSample)">
                                            <v-icon size="16" class="mr-1">mdi-eye</v-icon>
                                            Ver detalle
                                        </v-btn>
                                    </v-col>
                                </v-row>

                                <!-- Empty state -->
                                <div v-if="sortedMeasurements.length === 0" class="text-center pa-8">
                                    <v-icon size="48" color="grey">mdi-chart-line</v-icon>
                                    <div class="text-h6 mt-2 text-grey">No hay mediciones disponibles</div>
                                    <div class="text-body-2 text-grey">Las mediciones aparecerán aquí cuando se
                                        registren datos</div>
                                </div>
                            </div>

                            <!-- Ver más/menos button -->
                            <v-btn v-if="sortedMeasurements.length > 5" color="primary" block class="mt-4"
                                @click="showAllMeasurements = !showAllMeasurements">
                                {{ showAllMeasurements ? 'Ver menos' : `Ver todas (${sortedMeasurements.length})` }}
                            </v-btn>
                        </div>
                    </v-card-text>
                </v-card>
            </v-col>
            <!-- Images and description - Fixed column -->
            <v-col cols="12" md="6" class="fixed-column mb-6">
                <v-card class="rounded-lg overflow-hidden" elevation="3">
                    <!-- Carousel Header -->
                    <div class="position-relative">

                        <v-img :src="carouselImages[currentSlide]" height="500" cover class="gradient-overlay fade-img">
                            <template v-slot:placeholder>
                                <v-row class="fill-height ma-0" align="center" justify="center">
                                    <v-progress-circular indeterminate color="grey-lighten-5"></v-progress-circular>
                                </v-row>
                            </template>
                        </v-img>

                        <!-- Small indicator dots -->
                        <div class="carousel-indicators">
                            <div v-for="(_, index) in carouselImages" :key="index" class="indicator-dot"
                                :class="{ active: index === currentSlide }"></div>
                        </div>

                        <!-- Avatar that overlaps image and card content -->

                        <v-avatar size="80" class="avatar-overlap">
                            <v-avatar :color="device.iconBg" size="72">
                                <v-icon :color="device.iconColor" size="36">{{ device.icon }}</v-icon>
                            </v-avatar>
                        </v-avatar>
                    </div>

                    <!-- Card content with spacing for avatar -->
                    <v-card-item class="pt-8 mt-6">
                        <v-card-title class="text-h6 font-weight-bold px-0">
                            {{ currentDevice?.name || device.name }}
                        </v-card-title>
                        <v-card-subtitle class="px-0">
                            {{ currentDevice?.description || device.location }}
                        </v-card-subtitle>
                    </v-card-item>

                    <!-- Sensor info and Photo timing -->
                    <v-card-text class="px-4 pb-0">
                        <div class="d-flex">
                            <v-sheet class="pa-3 rounded bg-grey-lighten-4 flex-grow-1 mr-2">
                                <div class="text-body-1 font-weight-medium">
                                    {{ computedGrowthDataFromMeasurements.length }}
                                </div>
                                <div class="text-caption text-grey">Mediciones Analizadas</div>
                            </v-sheet>

                            <v-sheet class="pa-3 rounded bg-grey-lighten-4 flex-grow-1">
                                <div class="d-flex flex-column">
                                    <div class="text-body-1 font-weight-medium">
                                        Creado: {{ currentDevice ? new
                                            Date(currentDevice.createdAt).toLocaleDateString('es-ES') :
                                            'N/A' }}
                                    </div>
                                    <div class="text-body-2 font-weight-medium">
                                        Periodicidad: {{ currentDevice?.periodicity || 0 }} min
                                    </div>
                                </div>
                            </v-sheet>
                        </div>
                    </v-card-text>

                    <!-- Description -->
                    <v-card-text class="pt-3">
                        <p class="text-body-2">
                            {{ currentDevice?.extendedName || 'Dispositivo' }} -
                            {{ currentDevice?.active ? 'Activo' : 'Inactivo' }}.
                            Ubicación: {{ currentDevice?.lat }}, {{ currentDevice?.lng }}
                        </p>
                    </v-card-text>

                    <!-- Divider -->
                    <v-divider></v-divider>

                    <!-- Capture Progress and Analytics -->
                    <v-card-text>
                        <div class="d-flex justify-space-between align-center">
                            <div>
                                Mediciones: {{ deviceMeasurements.length }}
                            </div>
                            <v-chip :color="currentDevice?.active ? '#4CAF50' : '#FF9800'" text-color="white"
                                size="small" class="rounded px-2">
                                {{ currentDevice?.active ? 'Activo' : 'Inactivo' }}
                            </v-chip>
                        </div>

                        <div class="mt-2 d-flex justify-space-between align-center">
                            <div>Score de salud: {{ realSensorData.lastValues?.oxygen || 0 }}/30%</div>
                            <div class="text-body-2">
                                {{deviceMeasurements.filter(m => m.managed).length}}/{{ deviceMeasurements.length }}
                                Analizadas
                            </div>
                        </div>

                        <v-progress-linear
                            :model-value="(deviceMeasurements.filter(m => m.managed).length / Math.max(deviceMeasurements.length, 1)) * 100"
                            height="8" rounded color="primary" class="mt-1"></v-progress-linear>
                    </v-card-text>

                    <!-- Status and Updates -->
                    <v-card-text class="d-flex justify-space-between pt-0">
                        <div class="d-flex align-center">
                            <v-icon :color="currentDevice?.active ? 'green' : 'red'" size="small" class="mr-1">
                                {{ currentDevice?.active ? 'mdi-check-circle' : 'mdi-pause-circle' }}
                            </v-icon>
                            <span>{{ currentDevice?.active ? 'Activo' : 'Inactivo' }}</span>
                        </div>
                        <v-spacer></v-spacer>
                        <div class="d-flex align-center">
                            <span class="mr-2">Batería</span>
                            <v-icon size="small" class="mr-1">mdi-battery</v-icon>
                            <span>{{ device.battery.level }}%</span>
                        </div>
                    </v-card-text>
                </v-card>
            </v-col>


        </v-row>

        <!-- Modal Detalle de Medición -->
        <v-dialog v-model="showSampleDialog" max-width="700">
            <v-card v-if="selectedSample">
                <v-card-title class="text-h5 px-6 py-4 bg-primary text-white">
                    <v-icon class="mr-2">mdi-eye</v-icon>
                    Detalle de Medición
                </v-card-title>

                <v-card-text class="pa-6">
                    <!-- Imagen -->
                    <div v-if="selectedSample.imageUrl" class="position-relative mb-4">
                        <v-img :src="selectedSample.imageUrl" max-height="350" class="rounded-lg" cover>
                            <template v-slot:placeholder>
                                <v-row class="fill-height ma-0" align="center" justify="center">
                                    <v-progress-circular indeterminate color="primary"></v-progress-circular>
                                </v-row>
                            </template>
                        </v-img>
                        <v-btn icon size="small" color="white" class="position-absolute"
                            style="top: 8px; right: 8px;" :href="selectedSample.imageUrl" target="_blank">
                            <v-icon size="18">mdi-open-in-new</v-icon>
                            <v-tooltip activator="parent" location="top">Abrir imagen en nueva pestaña</v-tooltip>
                        </v-btn>
                    </div>

                    <!-- Info general -->
                    <v-alert type="info" variant="tonal" class="mb-4">
                        <div class="text-body-2">
                            <strong>Fecha:</strong> {{ new Date(selectedSample.time).toLocaleString('es-ES') }}<br>
                            <strong>Estado:</strong> {{ selectedSample.managed ? 'Analizada' : 'Pendiente' }}
                        </div>
                    </v-alert>

                    <!-- Datos del análisis -->
                    <div v-if="selectedSample.managed">
                        <v-row class="mb-4">
                            <v-col cols="6" md="3">
                                <v-card variant="outlined" class="pa-3 text-center">
                                    <div class="text-h5 font-weight-bold text-primary">{{ selectedSample.growthPercent }}%</div>
                                    <div class="text-caption">Crecimiento</div>
                                </v-card>
                            </v-col>
                            <v-col cols="6" md="3">
                                <v-card variant="outlined" class="pa-3 text-center">
                                    <div class="text-h5 font-weight-bold text-success">{{ selectedSample.newRootsCount }}</div>
                                    <div class="text-caption">Raíces Nuevas</div>
                                </v-card>
                            </v-col>
                            <v-col cols="6" md="3">
                                <v-card variant="outlined" class="pa-3 text-center">
                                    <div class="text-h5 font-weight-bold" :style="{ color: selectedSample.getHealthScoreColor() }">
                                        {{ selectedSample.rootHealthScore }}/10
                                    </div>
                                    <div class="text-caption">Salud</div>
                                </v-card>
                            </v-col>
                            <v-col cols="6" md="3">
                                <v-card variant="outlined" class="pa-3 text-center">
                                    <div class="text-h6 font-weight-bold">{{ selectedSample.getGrowthCategory() }}</div>
                                    <div class="text-caption">Categoría</div>
                                </v-card>
                            </v-col>
                        </v-row>

                        <v-divider class="mb-4"></v-divider>

                        <v-list density="compact">
                            <v-list-item>
                                <v-list-item-title>Color de Raíces</v-list-item-title>
                                <v-list-item-subtitle>{{ selectedSample.rootColor || 'No especificado' }}</v-list-item-subtitle>
                            </v-list-item>
                            <v-list-item>
                                <v-list-item-title>Patrón de Crecimiento</v-list-item-title>
                                <v-list-item-subtitle>{{ selectedSample.growthPattern || 'No especificado' }}</v-list-item-subtitle>
                            </v-list-item>
                            <v-list-item>
                                <v-list-item-title>Dirección Dominante</v-list-item-title>
                                <v-list-item-subtitle>{{ selectedSample.dominantDirection || 'No especificado' }}</v-list-item-subtitle>
                            </v-list-item>
                            <v-list-item>
                                <v-list-item-title>Calidad de Imagen</v-list-item-title>
                                <v-list-item-subtitle>{{ selectedSample.imageQuality || 'No especificado' }}</v-list-item-subtitle>
                            </v-list-item>
                            <v-list-item>
                                <v-list-item-title>Presencia de Hongos</v-list-item-title>
                                <v-list-item-subtitle>
                                    <v-chip size="small" :color="selectedSample.fungalPresence ? 'error' : 'success'">
                                        {{ selectedSample.fungalPresence ? 'Sí' : 'No' }}
                                    </v-chip>
                                </v-list-item-subtitle>
                            </v-list-item>
                            <v-list-item v-if="selectedSample.observation">
                                <v-list-item-title>Observaciones</v-list-item-title>
                                <v-list-item-subtitle>{{ selectedSample.observation }}</v-list-item-subtitle>
                            </v-list-item>
                        </v-list>
                    </div>

                    <v-alert v-else type="warning" variant="tonal">
                        Esta medición aún no ha sido analizada.
                    </v-alert>
                </v-card-text>

                <v-card-actions class="px-6 pb-4">
                    <v-spacer></v-spacer>
                    <v-btn variant="text" @click="showSampleDialog = false">Cerrar</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<style scoped>
/* Layout styles for fixed right column */
.device-view-container {
    height: calc(100vh - 64px);
    overflow: hidden;
    padding: 0;
}

.device-layout {
    height: 100%;
    margin: 0;
    display: flex;
    flex-wrap: nowrap;
}

.scrollable-column {
    height: calc(100vh - 64px);
    overflow-y: auto;
    padding-right: 12px;
    flex: 0 0 50%;
    max-width: 50%;
}

.fixed-column {
    height: calc(100vh - 64px);
    overflow-y: auto;
    padding-left: 12px;
    padding-right: 24px;
    flex: 0 0 50%;
    max-width: 50%;
    position: sticky;
    top: 0;
}

/* Media query for smaller screens */
@media (max-width: 960px) {
    .device-view-container {
        height: auto;
        overflow: visible;
        padding: 16px;
    }

    .scrollable-column {
        height: auto;
        overflow-y: visible;
        padding-right: 12px;
        width: 100% !important;
        max-width: 100% !important;
    }

    .fixed-column {
        position: relative;
        top: auto;
        height: auto;
        width: 100% !important;
        max-width: 100% !important;
        padding-left: 12px;
        padding-right: 12px;
    }
}

.v-list-item {
    padding: 0;
}

.avatar-group {
    margin-right: -10px;
    border: 2px solid white;
}

.avatar-overlap {
    position: absolute;
    bottom: -40px;
    left: 24px;
    border: 4px solid white;
    background-color: white;
    z-index: 2;
}

.gradient-overlay {
    position: relative;
}

.gradient-overlay::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(to bottom, rgba(255, 255, 255, 0.1), rgba(0, 0, 0, 0.3));
}

.carousel-indicators {
    position: absolute;
    bottom: 10px;
    width: 100%;
    display: flex;
    justify-content: center;
    z-index: 1;
}

.indicator-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: rgba(255, 255, 255, 0.5);
    margin: 0 4px;
    transition: all 0.3s ease;
}

.indicator-dot.active {
    background-color: white;
    width: 10px;
    height: 10px;
}

.fade-img {
    transition: opacity 0.5s ease-in-out;
}

.sensor-readings-container {
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    padding: 16px;
    background-color: #fafafa;
}

.deviceSamples-container {
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    padding: 16px;
    background-color: #fafafa;
}

.deviceSample-row {
    border-radius: 4px;
    transition: background-color 0.2s ease;
    border-left: 3px solid transparent;
}

.deviceSample-row:hover {
    background-color: #f5f5f5;
}

.deviceSample-row.managed {
    border-left-color: #4CAF50;
    background-color: #e8f5e8;
}

.deviceSample-row.managed:hover {
    background-color: #c8e6c9;
}

.deviceSample-row.unmanaged {
    border-left-color: #FF9800;
    background-color: #fff3e0;
}

.deviceSample-row.unmanaged:hover {
    background-color: #ffe0b2;
}

.sensor-reading-row {
    padding: 8px;
    border-radius: 4px;
    transition: background-color 0.2s ease;
    border-left: 3px solid transparent;
}

.sensor-reading-row:hover {
    background-color: #f5f5f5;
}

.sensor-reading-row.with-growth {
    border-left-color: #9C27B0;
    background-color: #f3e5f5;
}

.sensor-reading-row.with-growth:hover {
    background-color: #e1bee7;
}
</style>